import React, { useState } from "react";
import { Box, Typography, TextField, MenuItem, Select, FormControl, InputLabel, Grid } from "@mui/material";
import { Button } from "@deere/fuel-react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Field from "../../../common/field";

const SearchFilters = ({ onSearch, onClear, dealerUnits, dealerCountries, user }) => {
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    dealerNumber: "",
    dealerCountry: "",
    dealerUnit: "",
    source: "",
  });
  const [dateRangeKey, setDateRangeKey] = useState(0); // Key to force DateRangePicker remount

  const isBNUser = user?.userRole === "BN_USER";

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateRangeChange = (dateRange) => {
    setFilters((prev) => ({
      ...prev,
      startDate: dateRange && dateRange[0] ? dateRange[0] : null,
      endDate: dateRange && dateRange[1] ? dateRange[1] : null,
    }));
  };

  const handleSearch = () => {
    onSearch(filters);
  };

  const handleClear = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      dealerNumber: "",
      dealerCountry: "",
      dealerUnit: "",
      source: "",
    };
    setFilters(clearedFilters);
    setDateRangeKey(prev => prev + 1); // Force DateRangePicker to remount and reset
    onClear();
  };

  return (
    <Box
      sx={{
        padding: "20px",
        backgroundColor: "white",
        marginBottom: "20px",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography
        variant="h3"
        sx={{ fontWeight: "bold", marginBottom: "15px", color: "black" }}
      >
        Search Filters
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} alignItems="flex-end">
          {/* Date Range */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Field
              key={dateRangeKey} // Force remount when cleared
              onChange={handleDateRangeChange}
              label="Billing Date Range"
              type={"search-date"}
              customWidth={true}
            />
          </Grid>

          {/* Dealer Number */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextField
              label="Dealer Number"
              value={filters.dealerNumber}
              onChange={(e) => handleFilterChange("dealerNumber", e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Dealer Country */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Dealer Country</InputLabel>
              <Select
                value={filters.dealerCountry}
                onChange={(e) => handleFilterChange("dealerCountry", e.target.value)}
                label="Dealer Country"
              >
                <MenuItem value="">All</MenuItem>
                {dealerCountries && dealerCountries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Dealer Unit */}
          <Grid item xs={12} sm={6} md={isBNUser ? 12 : 6} lg={isBNUser ? 5 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Dealer Unit</InputLabel>
              <Select
                value={filters.dealerUnit}
                onChange={(e) => handleFilterChange("dealerUnit", e.target.value)}
                label="Dealer Unit"
              >
                <MenuItem value="">All</MenuItem>
                {dealerUnits && dealerUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Source - Hidden for BN_USER */}
          {!isBNUser && (
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={filters.source}
                  onChange={(e) => handleFilterChange("source", e.target.value)}
                  label="Source"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="UPLOAD">Upload</MenuItem>
                  <MenuItem value="API">API</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Buttons - New Row */}
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
              <Button
                variant="primary"
                onClick={handleSearch}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                sx={{
                  color: "black",
                  borderColor: "#CCCCCC",
                  "&:hover": {
                    borderColor: "#CCCCCC",
                    backgroundColor: "#F0F0F0",
                  },
                }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </Box>
  );
};

export default SearchFilters;
